import { useState, useEffect, Fragment } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon, AcademicCapIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import { apiService } from '../services/apiService';

interface ClassInfo {
  id: string;
  name: string;
  code: string;
  term: string;
  studentCount?: number;
}

const ClassSelector = () => {
  const { classId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile } = useAuth();
  const [classes, setClasses] = useState<ClassInfo[]>([]);
  const [currentClass, setCurrentClass] = useState<ClassInfo | null>(null);
  const [, setLoading] = useState(false);

  // Only show class selector on class-related pages
  const isClassPage = location.pathname.includes('/dashboard/class/');
  
  useEffect(() => {
    const loadClasses = async () => {
      if (!user || !profile || profile.user_type !== 'professor' || !isClassPage) return;

      setLoading(true);
      try {
        const { data } = await apiService.getClassesByProfessor(user.id);
        if (data) {
          const formattedClasses: ClassInfo[] = data.map(cls => ({
            id: cls.id,
            name: cls.name,
            code: cls.code,
            term: cls.term,
            studentCount: 0, // TODO: Get actual count
          }));
          setClasses(formattedClasses);

          // Set current class if we're on a class page
          if (classId) {
            const current = formattedClasses.find(cls => cls.id === classId);
            setCurrentClass(current || null);
          }
        }
      } catch (error) {
        console.error('Failed to load classes:', error);
      } finally {
        setLoading(false);
      }
    };

    loadClasses();
  }, [user, profile, classId, isClassPage]);

  const handleClassSwitch = (newClassId: string) => {
    // Determine the appropriate route based on current location
    let newRoute = `/dashboard/class/${newClassId}`;
    
    if (location.pathname.includes('/insights')) {
      newRoute = `/dashboard/class/${newClassId}/insights`;
    } else if (location.pathname.includes('/feedback/setup')) {
      newRoute = `/dashboard/class/${newClassId}/feedback/setup`;
    } else if (location.pathname.includes('/feedback/reports')) {
      newRoute = `/dashboard/class/${newClassId}/feedback/reports`;
    } else if (location.pathname.includes('/feedback')) {
      newRoute = `/dashboard/class/${newClassId}/feedback`;
    }
    
    navigate(newRoute);
  };

  const handleCreateClass = () => {
    navigate('/dashboard');
  };

  // Don't render if not on a class page or if professor doesn't have classes
  if (!isClassPage || !currentClass || classes.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center space-x-3">
      <AcademicCapIcon className="h-5 w-5 text-emerald-600" />
      <Menu as="div" className="relative z-[10000]">
        <Menu.Button className="flex items-center space-x-2 text-white hover:text-lime-100 transition-colors bg-white/10 rounded-lg px-3 py-2 backdrop-blur-sm min-w-0 max-w-xs lg:max-w-sm">
          <div className="text-left min-w-0 flex-1">
            <div className="text-sm font-medium text-white truncate" title={currentClass.name}>
              {currentClass.name || 'Unnamed Class'}
            </div>
            <div className="text-xs text-lime-100 truncate">
              {currentClass.code || 'No Code'} • {currentClass.term || 'No Term'}
            </div>
          </div>
          <ChevronDownIcon className="h-4 w-4 text-lime-200 flex-shrink-0" />
        </Menu.Button>

        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="absolute left-0 mt-2 w-80 sm:w-96 origin-top-left divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-[9999]">
            <div className="px-4 py-3 border-b border-gray-100">
              <p className="text-sm font-medium text-gray-900">Switch to Class</p>
              <p className="text-xs text-gray-500">{classes.length} classes available</p>
            </div>

            <div className="py-1 max-h-64 overflow-y-auto">
              {classes.map((cls) => (
                <Menu.Item key={cls.id}>
                  {({ active }) => (
                    <button
                      onClick={() => handleClassSwitch(cls.id)}
                      className={`${
                        active ? 'bg-emerald-50 text-emerald-900' : 'text-gray-700'
                      } ${
                        cls.id === currentClass?.id ? 'bg-emerald-100 text-emerald-900 font-medium' : ''
                      } group flex w-full items-center px-4 py-3 text-sm`}
                    >
                      <div className="flex-1 text-left min-w-0">
                        <div 
                          className="font-medium text-gray-900 truncate" 
                          title={cls.name}
                        >
                          {cls.name || 'Unnamed Class'}
                        </div>
                        <div className="text-xs text-gray-500 mt-1 truncate">
                          {cls.code || 'No Code'} • {cls.term || 'No Term'}
                          {cls.studentCount !== undefined && (
                            <span className="ml-2">• {cls.studentCount} students</span>
                          )}
                        </div>
                      </div>
                      {cls.id === currentClass?.id && (
                        <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                      )}
                    </button>
                  )}
                </Menu.Item>
              ))}
            </div>

            <div className="py-1">
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={handleCreateClass}
                    className={`${
                      active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                    } group flex w-full items-center px-4 py-2 text-sm`}
                  >
                    <PlusIcon className="mr-3 h-4 w-4" />
                    Create New Class
                  </button>
                )}
              </Menu.Item>
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
    </div>
  );
};

export default ClassSelector;